import React, { Fragment, ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Translations } from './configuration';
import {
  AppDatePicker,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import VatReturnListActions from 'redux/reducers/vatReturn_list_reducer/actions';
import * as VatReturnListSelectors from 'redux/reducers/vatReturn_list_reducer/selectors';
import * as DateUtils from 'utils/dates';
import { StateStatus } from 'redux/utils/common';

interface FormFields {
  amount: number | null;
  cents: number | null;
  paymentDate: Date | null;
}

interface IElementsProps {
  size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  element: ReactElement;
  id: string;
  rowStyle?: any;
}

interface IProps {
  vat_number: string;
}

export default function AddPaymentForm(props: IProps): ReactElement {
  // console.log('Render AddPaymentForm');
  const dispatch = useDispatch();
  const defaultValues: FormFields = {
    amount: null,
    cents: null,
    paymentDate: null,
  };

  const status = VatReturnListSelectors.useGetAddVatReturnPaymentStatus();

  const { errors, control, handleSubmit, watch, setError } =
    useForm<FormFields>({
      defaultValues,
      mode: 'onTouched',
    });
  const classes = useStyles();

  const onSubmit = (data: any) => {
    // console.log(data.amount, data.cents, data.amount + data.cents / 100);

    let payment_amount =
      Number.parseInt(data.amount) + Number.parseInt(data.cents) / 100;
    if (payment_amount <= 0) {
      setError('amount', {
        message: 'Payment amount should be greater than 0!',
        shouldFocus: true,
      });
      return;
    }

    dispatch(
      VatReturnListActions.addVatReturnPaymentAction({
        vat_return_id: props.vat_number,
        amount:
          Number.parseInt(data.amount) + Number.parseInt(data.cents) / 100,
        payment_date: DateUtils.convertToUtc(data.paymentDate),
      })
    );
  };

  const gridElements = (): IElementsProps[] => {
    return [
      {
        element: renderAmountField(errors, control),
        size: 10,
        id: 'amount',
      },
      {
        element: renderCentsField(errors, control),
        size: 2,
        id: 'cents',
      },
      {
        element: renderPaymentDateField(errors, control),
        size: 12,
        id: 'paymentDate',
      },
      {
        element: (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            style={{ width: '100%' }}
            disabled={status && status === StateStatus.Pending}
          >
            <TranslatedText textMap={Translations.add} />
          </Button>
        ),
        size: 12,
        id: 'action',
      },
    ];
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout elements={gridElements()} />
    </form>
  );
}

const renderAmountField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <Fragment>
      <AppTextField
        variant="outlined"
        margin="normal"
        type={'number'}
        required
        fullWidth
        id="amount"
        label={<TranslatedText textMap={Translations.amount_control} />}
        error={'amount' in errors || 'cents' in errors}
        name="amount"
        control={control}
        rules={{
          required: 'Required Field',
          min: {
            value: 0,
            message: Translations.negative_amount_error.English,
          },
        }}
        helperText={errors.amount && errors.amount.message}
      />
    </Fragment>
  );
};

const renderCentsField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      type="number"
      id="cents"
      label={<TranslatedText textMap={Translations.cents_control} />}
      error={'cents' in errors}
      name="cents"
      control={control}
      rules={{
        required: 'Required',
        min: {
          value: 0,
          message: Translations.invalid_cents_error.English,
        },
        maxLength: {
          value: 2,
          message: Translations.invalid_cents_error.English,
        },
      }}
      helperText={errors.cents && errors.cents.message}
    />
  );
};

const renderPaymentDateField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      autoOk
      margin="normal"
      label={<TranslatedText textMap={Translations.payment_date_control} />}
      error={'paymentDate' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      name="paymentDate"
      helperText={errors.paymentDate && errors.paymentDate.message}
    />
  );
};
